import { StandardEvents } from '@shopify/events';

/**
 * <mineralia-variant-sync data-sync-id="unique-id">
 *
 * Refreshes its own content when the product variant changes. It reuses the section HTML the
 * variant picker already fetches (ProductSelectEvent promise), so it does not make extra requests.
 * Emits `mineralia:variant-synced` (bubbles) after updating, with `detail.variant` (variant JSON or null).
 */
class MineraliaVariantSync extends HTMLElement {
  /** @type {Element | null} */
  #scope = null;

  connectedCallback() {
    this.#scope = this.closest('.shopify-section, dialog');
    this.#scope?.addEventListener(StandardEvents.productSelect, this.#onProductSelect);
  }

  disconnectedCallback() {
    this.#scope?.removeEventListener(StandardEvents.productSelect, this.#onProductSelect);
    this.#scope = null;
  }

  /** @param {Event & { promise?: Promise<any> }} event */
  #onProductSelect = (event) => {
    if (event.target instanceof Element && event.target.closest('product-card')) return;

    event.promise
      ?.then(({ detail } = {}) => {
        const html = detail?.html;
        if (!html || !this.isConnected) return;

        const id = this.dataset.syncId;
        if (!id) return;

        const next = html.querySelector(`mineralia-variant-sync[data-sync-id="${CSS.escape(id)}"]`);
        if (!next) return;

        for (const attribute of [...this.attributes]) {
          if (attribute.name.startsWith('data-') && !next.hasAttribute(attribute.name)) {
            this.removeAttribute(attribute.name);
          }
        }
        for (const attribute of [...next.attributes]) {
          if (attribute.name.startsWith('data-')) this.setAttribute(attribute.name, attribute.value);
        }

        this.innerHTML = next.innerHTML;
        this.hidden = next.hidden;

        this.dispatchEvent(
          new CustomEvent('mineralia:variant-synced', {
            bubbles: true,
            detail: { variant: detail.resource ?? null },
          })
        );
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[mineralia-variant-sync]', error);
      });
  };
}

if (!customElements.get('mineralia-variant-sync')) {
  customElements.define('mineralia-variant-sync', MineraliaVariantSync);
}
